import { API_BASE_URL, APP_HEADERS } from './env';

/**
 * HTTP error with status — 401/403 render the access-denied screen.
 *
 * `body` carries the parsed error payload because some non-2xx answers are
 * part of a protocol rather than a dead end: PATCH /bids/:id/status replies
 * 409 `{requiresConfirmation, existingWinners}` and the caller needs those
 * winners to render the confirmation before re-issuing the request.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly body: unknown = undefined,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  searchParams?: Record<string, string | undefined>;
  /** Extra request headers, e.g. `Idempotency-Key` on a money-recording POST. */
  headers?: Record<string, string>;
}

/** Build the absolute api-node URL for a path + optional query params. */
export function buildUrl(path: string, searchParams?: Record<string, string | undefined>): string {
  const url = new URL(`${API_BASE_URL}/${path.replace(/^\/+/, '')}`);
  for (const [key, value] of Object.entries(searchParams ?? {})) {
    if (value !== undefined) url.searchParams.set(key, value);
  }
  return url.toString();
}

const UNSAFE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/** Exact body CsrfGuard throws (apps/api/src/common/guards/csrf.guard.ts) — the
 * one signal that means "the token is stale/missing", not "you lack permission". */
const CSRF_ERROR_MESSAGE = 'CSRF token invalid or missing';

/**
 * In-memory double-submit CSRF token, minted by GET /api/auth/csrf-token
 * (apps/api/src/modules/auth/csrf-token.controller.ts). Never persisted —
 * every fresh tab/reload re-mints it lazily on the first mutation. The
 * matching cookie is httpOnly and set by that same response; this value is
 * only ever echoed back as the `x-csrf-token` header.
 */
let csrfToken: string | null = null;
let csrfTokenPromise: Promise<string> | null = null;

async function fetchCsrfToken(): Promise<string> {
  const response = await fetch(`${API_BASE_URL}/auth/csrf-token`, {
    credentials: 'include',
    headers: APP_HEADERS,
  });
  const data: unknown = await response.json().catch(() => ({}));
  if (!response.ok) {
    const record = (data ?? {}) as Record<string, unknown>;
    const message =
      (typeof record.error === 'string' && record.error) ||
      (typeof record.message === 'string' && record.message) ||
      `HTTP ${response.status}`;
    throw new ApiError(response.status, message, data);
  }
  const token = (data as { csrfToken?: unknown }).csrfToken;
  if (typeof token !== 'string' || !token) {
    throw new ApiError(response.status, 'CSRF token endpoint returned no token', data);
  }
  csrfToken = token;
  return token;
}

/** Reuse the cached token; mint one only when there isn't one yet, and only once per burst of concurrent callers. */
async function ensureCsrfToken(): Promise<string> {
  if (csrfToken) return csrfToken;
  if (!csrfTokenPromise) {
    csrfTokenPromise = fetchCsrfToken().finally(() => {
      csrfTokenPromise = null;
    });
  }
  return csrfTokenPromise;
}

async function send(
  path: string,
  method: string,
  body: unknown,
  searchParams: Record<string, string | undefined> | undefined,
  csrfHeader: string | undefined,
  extraHeaders: Record<string, string> = {},
): Promise<Response> {
  const headers: Record<string, string> = { ...extraHeaders, ...APP_HEADERS };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (csrfHeader) headers['x-csrf-token'] = csrfHeader;

  return fetch(buildUrl(path, searchParams), {
    method,
    credentials: 'include',
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
}

/**
 * Same CSRF attach-and-retry-once contract as apiFetch, for callers that
 * cannot go through it because their body isn't JSON — today that's exactly
 * one caller: uploadUserDocument's multipart POST (apiFetch always sets
 * `Content-Type: application/json`, which would corrupt a FormData body).
 * Returns the raw Response so the caller keeps its own parsing/error shape.
 */
export async function fetchWithCsrf(url: string, init: RequestInit): Promise<Response> {
  const attempt = async (): Promise<Response> => {
    const token = await ensureCsrfToken();
    return fetch(url, {
      ...init,
      credentials: 'include',
      headers: {
        ...(init.headers as Record<string, string> | undefined),
        ...APP_HEADERS,
        'x-csrf-token': token,
      },
    });
  };

  let response = await attempt();
  if (response.status === 403) {
    const probe = (await response.clone().json().catch(() => ({}))) as Record<string, unknown>;
    if (probe.error === CSRF_ERROR_MESSAGE) {
      csrfToken = null;
      response = await attempt();
    }
  }
  return response;
}

/**
 * Fetch wrapper: session cookie included, JSON in/out, non-2xx → ApiError
 * with the server's `error`/`message` body field when present.
 *
 * Every mutating method (POST/PUT/PATCH/DELETE) attaches the double-submit
 * `x-csrf-token` header CsrfGuard requires in production. A token is minted
 * lazily on the first mutation of a session and reused after that; if the
 * server ever answers with the guard's exact "CSRF token invalid or
 * missing" body (stale/rotated cookie), the cached token is dropped and the
 * request is retried exactly once with a freshly minted one — any other
 * 403 (a real authorization failure) is surfaced as-is, not retried.
 */
export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, searchParams, headers } = options;
  const isUnsafe = UNSAFE_METHODS.has(method);

  let response = await send(
    path,
    method,
    body,
    searchParams,
    isUnsafe ? await ensureCsrfToken() : undefined,
    headers,
  );

  if (isUnsafe && response.status === 403) {
    const probe = (await response.clone().json().catch(() => ({}))) as Record<string, unknown>;
    if (probe.error === CSRF_ERROR_MESSAGE) {
      csrfToken = null;
      response = await send(path, method, body, searchParams, await ensureCsrfToken(), headers);
    }
  }

  const data: unknown = await response.json().catch(() => ({}));

  if (!response.ok) {
    const record = (data ?? {}) as Record<string, unknown>;
    const message =
      (typeof record.error === 'string' && record.error) ||
      (typeof record.message === 'string' && record.message) ||
      `HTTP ${response.status}`;
    throw new ApiError(response.status, message, data);
  }

  return data as T;
}
